/**
 * LA FOTO SE ESTAMPA: SIN SELLO NO PRUEBA NADA.
 *
 * Una foto suelta no dice de qué orden es, ni dónde ni cuándo se tomó, ni quién
 * la tomó. Cuando el cliente reclama —o cuando hay que sustentar un cobro— eso
 * es exactamente lo que se necesita. Aquí la foto sale de la cámara y se le
 * quema encima el sello: orden, fecha y hora, coordenadas, dirección y técnico.
 *
 * Se quema en los píxeles a propósito. Los metadatos EXIF se pierden al subir,
 * al comprimir o al reenviar por WhatsApp; lo dibujado sobrevive a todo eso y
 * viaja con la imagen hasta el acta que firma el cliente.
 */

import { type LecturaUbicacion, posicionPrecisa } from '@/lib/nativo/ubicacion'

/**
 * Tope del lado largo de la foto que se sube, en píxeles. Una evidencia legible
 * para el acta no necesita los 3000-4000 px del sensor; sobre datos móviles cada
 * MB de más es tiempo que el técnico espera. El sello se mide contra el ancho,
 * así que se ve igual a cualquier tamaño.
 */
const MAX_LADO = 1600

/** Calidad del JPEG final. 0.7 baja bien el peso sin que el sello pierda nitidez. */
const CALIDAD_JPEG = 0.7

/** Lo que va escrito en la foto. */
export type SelloFoto = {
  ot: string
  direccion?: string | null
  zona?: string | null
  tecnico?: string | null
  latitud?: number | null
  longitud?: number | null
  /** Precisión del GPS en metros, si se conoce. */
  exactitudM?: number | null
  /**
   * La dirección que Google reconoce en el punto del GPS. Cuando la hay, se
   * escribe en lugar de las coordenadas en grados: es lo que una persona lee.
   * Las coordenadas quedan igual, en una línea corta, como respaldo.
   */
  sitioGps?: string | null
}

/** Grados, minutos y segundos: como los escriben las cámaras de campo. */
function aGMS(valor: number, esLatitud: boolean): string {
  const hemisferio = esLatitud ? (valor >= 0 ? 'N' : 'S') : valor >= 0 ? 'E' : 'W'
  const abs = Math.abs(valor)
  const grados = Math.floor(abs)
  const minutos = Math.floor((abs - grados) * 60)
  const segundos = ((abs - grados) * 60 - minutos) * 60
  return `${grados}°${String(minutos).padStart(2, '0')}'${segundos.toFixed(3)}"${hemisferio}`
}

/** Fecha y hora de Colombia, que es donde se toma la foto. */
function fechaHoraLocal(d: Date): string {
  return new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d)
}

/**
 * La posición de ahora. Si el GPS no responde, la foto igual se estampa.
 *
 * Dentro del APK va por el plugin nativo (obtenerPosicionActual); en la web,
 * por navigator.geolocation. Antes usaba navigator.geolocation directo, que en
 * el WebView de Android da «permiso negado» aunque el permiso esté concedido.
 */
export function posicionParaSello(): Promise<LecturaUbicacion | null> {
  // La mejor en unos segundos, no la primera: la primera traía «± 100 m»
  // (ver mejorPosicion). Si la cámara la calentó, ya está lista.
  return posicionPrecisa()
}

function leerImagen(archivo: File): Promise<HTMLImageElement> {
  return new Promise((resolver, rechazar) => {
    const url = URL.createObjectURL(archivo)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolver(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      rechazar(new Error('No se pudo leer la foto.'))
    }
    img.src = url
  })
}

/**
 * Devuelve la misma foto con el sello quemado encima.
 *
 * Si algo falla —un canvas bloqueado, una imagen que no carga— devuelve la foto
 * original: es preferible una evidencia sin sello que quedarse sin evidencia.
 */
export async function estamparFoto(archivo: File, sello: SelloFoto): Promise<File> {
  try {
    const img = await leerImagen(archivo)
    const anchoOrig = img.naturalWidth
    const altoOrig = img.naturalHeight
    if (!anchoOrig || !altoOrig) return archivo

    // Se reduce el lado largo al tope antes de dibujar. Si la foto ya es más
    // chica (la cámara nativa entrega ~1600 px), la escala es 1 y no toca nada.
    const escala = Math.min(1, MAX_LADO / Math.max(anchoOrig, altoOrig))
    const ancho = Math.round(anchoOrig * escala)
    const alto = Math.round(altoOrig * escala)

    const lienzo = document.createElement('canvas')
    lienzo.width = ancho
    lienzo.height = alto
    const ctx = lienzo.getContext('2d')
    if (!ctx) return archivo

    // Suaviza el reescalado para que la foto no salga dentada al encoger.
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, 0, 0, ancho, alto)

    // Todo se mide contra el ancho para que el sello se vea igual en una foto
    // de 1600 px que en una de 4000.
    const base = ancho / 100
    const cuerpo = Math.max(11, base * 2.4)
    const margen = base * 2.2
    const alturaLinea = cuerpo * 1.42

    // ── El número de la orden, arriba a la izquierda ────────────────────────
    ctx.font = `700 ${cuerpo * 1.15}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`
    const textoOt = sello.ot
    const anchoOt = ctx.measureText(textoOt).width
    const padOt = cuerpo * 0.55

    ctx.fillStyle = 'rgba(255,255,255,0.94)'
    ctx.fillRect(margen, margen, anchoOt + padOt * 2, cuerpo * 1.15 + padOt * 1.6)
    ctx.fillStyle = '#180935'
    ctx.textBaseline = 'top'
    ctx.fillText(textoOt, margen + padOt, margen + padOt * 0.8)

    // ── El detalle, abajo a la derecha ──────────────────────────────────────
    const lineas: string[] = [fechaHoraLocal(new Date())]

    if (sello.latitud != null && sello.longitud != null) {
      // DÓNDE ESTÁ PARADO EL TÉCNICO, no dónde queda el trabajo.
      //
      // La dirección la resuelve Google al revés desde el punto del GPS: es el
      // sitio real de la foto, que es lo que prueba que se hizo la visita. Ese
      // es el dato que manda y por eso va primero.
      //
      // Si Google no contestó a tiempo —señal mala en obra— hay dos respaldos,
      // en este orden: la dirección que el técnico escribió al levantar el
      // trabajo (aproximada, pero legible) y, en último caso, los grados. Antes
      // se caía directo a los grados, y una foto que dice «4°44'3\"N» no la lee
      // nadie. Los grados no desaparecen: van igual en la línea de abajo como
      // prueba exacta del punto.
      const sitio =
        sello.sitioGps ??
        sello.direccion ??
        `${aGMS(sello.latitud, true)} ${aGMS(sello.longitud, false)}`
      lineas.push(sitio)

      // Las coordenadas exactas, en una sola línea corta, como respaldo de la
      // evidencia: la dirección es de Google, el punto es del GPS.
      const coord = `${sello.latitud.toFixed(6)}, ${sello.longitud.toFixed(6)}`
      lineas.push(
        sello.exactitudM != null ? `${coord} · ±${Math.round(sello.exactitudM)} m` : coord,
      )
    } else {
      lineas.push('Sin señal de GPS')
      // Sin punto no hay sitio real; queda la dirección del trabajo si la hay.
      if (sello.direccion) lineas.push(sello.direccion)
    }

    lineas.push([sello.zona || 'Cartago', 'Valle del Cauca'].join(', '))
    if (sello.tecnico) lineas.push(`Técnico: ${sello.tecnico}`)
    lineas.push('MEGA Distribuciones y Proyectos S.A.S.')

    ctx.font = `600 ${cuerpo}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`
    ctx.textAlign = 'right'
    ctx.textBaseline = 'alphabetic'

    const altoBloque = lineas.length * alturaLinea
    const arriba = alto - margen - altoBloque

    // Un velo oscuro detrás: sobre una pared blanca el texto blanco desaparece.
    const degradado = ctx.createLinearGradient(0, arriba - alturaLinea, 0, alto)
    degradado.addColorStop(0, 'rgba(0,0,0,0)')
    degradado.addColorStop(1, 'rgba(0,0,0,0.55)')
    ctx.fillStyle = degradado
    ctx.fillRect(0, arriba - alturaLinea, ancho, alto - arriba + alturaLinea)

    ctx.shadowColor = 'rgba(0,0,0,0.85)'
    ctx.shadowBlur = cuerpo * 0.4
    ctx.fillStyle = '#FFFFFF'

    lineas.forEach((linea, i) => {
      ctx.fillText(linea, ancho - margen, arriba + alturaLinea * (i + 1))
    })
    ctx.shadowBlur = 0

    const blob = await new Promise<Blob | null>((r) => lienzo.toBlob(r, 'image/jpeg', CALIDAD_JPEG))
    if (!blob) return archivo

    return new File([blob], `evidencia-${sello.ot}-${Date.now()}.jpg`, { type: 'image/jpeg' })
  } catch {
    // Evidencia sin sello antes que quedarse sin evidencia.
    return archivo
  }
}
